// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { responseMetadataSchema } from '@/api/types';
import { sortDirectionSchema } from '@/constants/sort';
import { sparseItemSchema, sparsePersonSchema } from '@/sparse/api/types';
import { dateTimeSchema } from '@/utilities/date-time-schema';
import { searchTermSchema } from '@/utilities/search-term';
import { z } from 'zod';

/**
 * Schema for a single bundle member line (item + default quantity). The
 * member item is the platform's shared sparse item shape.
 */
const bundleMemberSchema = z.object({
  item: sparseItemSchema,
  defaultQty: z.number(),
  position: z.number(),
});

/**
 * Schema for a bundle in the catalog.
 */
export const bundleSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  isArchived: z.boolean(),
  createdAt: dateTimeSchema,
  lastUpdatedAt: dateTimeSchema,
  lastUpdatedBy: sparsePersonSchema,
  members: z.array(bundleMemberSchema),
});

export type Bundle = z.infer<typeof bundleSchema>;

/**
 * Shared envelope schema for endpoints that return a single bundle.
 */
export const bundleResponseSchema = z
  .object({ data: bundleSchema, metadata: responseMetadataSchema })
  .transform(({ data, ...response }) => ({ ...response, bundle: data }));

export type BundleResponse = z.infer<typeof bundleResponseSchema>;

/**
 * A bundle member line for create and update requests.
 */
export interface BundleMemberInput {
  itemId: string;
  defaultQty: number;
}

/**
 * Schema defining valid properties for sorting bundles server-side.
 */
const bundleSortPropertySchema = z.enum(['name', 'lastUpdatedAt']);

export type BundleSortProperty = z.infer<typeof bundleSortPropertySchema>;

/**
 * URL search params schema for the bundles list route.
 */
export const bundlesSearchParamsSchema = z.object({
  query: searchTermSchema,
  archived: z.boolean().optional(),
  sort: bundleSortPropertySchema.optional(),
  direction: sortDirectionSchema.optional(),
  page: z.number().optional(),
  new: z.string().optional(),
});

export type BundlesSearchParams = z.infer<typeof bundlesSearchParamsSchema>;
